import axios from 'axios';
import getCroppedImg from './cropImage';

// =============================================
// AVATAR DISPLAY URL HELPER
// =============================================
const getAvatarDisplayUrl = (avatarUrl) => {
    if (!avatarUrl) return '';
    const cleaned = String(avatarUrl).trim().replace(/^['"]|['"]$/g, '');
    if (!cleaned) return '';
    if (/^https?:\/\//i.test(cleaned)) return cleaned;
    if (cleaned.startsWith('/api/')) return cleaned;
    if (cleaned.startsWith('api/')) return `/${cleaned}`;
    return cleaned.startsWith('/') ? `/api${cleaned}` : `/api/${cleaned}`;
};

export const refreshCurrentAvatarFromServer = async ({ chatbotTitle, fallbackUrl = '', setFormData }) => {
    if (!chatbotTitle) return;

    try {
        const response = await axios.get('/api/data');
        const savedAvatar = response.data?.[chatbotTitle]?.avatar_url || '';
        const canonicalAvatar = (savedAvatar || fallbackUrl || '').split('?')[0];

        if (canonicalAvatar) {
            setFormData(prev => ({
                ...prev,
                avatar_url: `${canonicalAvatar}?t=${Date.now()}`,
                image: undefined
            }));
        }
    } catch (error) {
        if (fallbackUrl) {
            const canonicalAvatar = fallbackUrl.split('?')[0];
            if (canonicalAvatar) {
                setFormData(prev => ({
                    ...prev,
                    avatar_url: `${canonicalAvatar}?t=${Date.now()}`,
                    image: undefined
                }));
            }
        }
    }
};

export const uploadAvatarAndPersist = async ({
    file,
    selectedPromptTitle,
    formData,
    setFormData,
    setStatusMessage,
    fetchPrompts,
    refreshAvatar
}) => {
    if (!selectedPromptTitle) {
        setStatusMessage('Please save the chatbot first before uploading an avatar.');
        return false;
    }

    try {
        setStatusMessage('Uploading avatar...');
        if (formData.avatar_url) {
            const clean = formData.avatar_url.split('?')[0];
            try {
                await axios.post('/api/delete_avatar', { avatar_url: clean });
            } catch (error) {
                console.warn('Previous avatar file missing or could not be deleted:', error);
            }
        }

        const imgData = new FormData();
        imgData.append('avatar', file);
        imgData.append('chatbot_name', selectedPromptTitle);

        const uploadRes = await axios.post('/api/upload_avatar', imgData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        if (!uploadRes.data || !uploadRes.data.avatar_url) {
            setStatusMessage('Error: Failed to upload avatar image.');
            return false;
        }

        const avatar_url = `${uploadRes.data.avatar_url}?t=${Date.now()}`;
        setFormData(prev => ({ ...prev, avatar_url, image: undefined }));

        const payload = {
            title: selectedPromptTitle,
            name: formData.name || '',
            language: formData.language || '',
            level: formData.level || '',
            voice: formData.voice || 'alloy',
            initialText: formData.initialText || '',
            prompt: formData.prompt || '',
            instructions: formData.instructions || '',
            avatar_url,
            use_rag: formData.use_rag || false
        };

        const response = await axios.post('/api/update_prompt', payload);
        setStatusMessage(`Success: ${response.data.message}`);
        await refreshAvatar({ chatbotTitle: selectedPromptTitle, fallbackUrl: avatar_url, setFormData });
        await fetchPrompts();
        return true;
    } catch (error) {
        console.error('Failed to upload avatar:', error);
        setStatusMessage(`Error uploading avatar: ${error.response?.data?.error || error.message}`);
        return false;
    }
};

export const clearAvatarAndPersist = async ({
    selectedPromptTitle,
    formData,
    setFormData,
    setPendingAvatarDelete,
    fetchPrompts,
    setStatusMessage
}) => {
    if (!formData.avatar_url) {
        return false;
    }

    try {
        const clean = formData.avatar_url.split('?')[0];
        let deleteFailed = false;
        try {
            await axios.post('/api/delete_avatar', { avatar_url: clean });
        } catch (error) {
            deleteFailed = true;
            console.warn('Avatar file missing or could not be deleted:', error);
        }

        const payload = {
            title: selectedPromptTitle,
            name: formData.name || '',
            language: formData.language || '',
            level: formData.level || '',
            voice: formData.voice || 'alloy',
            initialText: formData.initialText || '',
            prompt: formData.prompt || '',
            instructions: formData.instructions || '',
            avatar_url: '',
            use_rag: formData.use_rag || false
        };
        await axios.post('/api/update_prompt', payload);

        setFormData(prev => ({ ...prev, avatar_url: '' }));
        setPendingAvatarDelete(false);
        await fetchPrompts();
        setStatusMessage(
            deleteFailed
                ? 'Avatar file was missing, but the avatar reference was cleared.'
                : 'Avatar deleted.'
        );
        return true;
    } catch (error) {
        console.error('Failed to delete avatar:', error);
        setStatusMessage(`Error deleting avatar: ${error.response?.data?.error || error.message}`);
        return false;
    }
};

export const prepareAvatarForPromptSubmit = async ({
    formData,
    setFormData,
    pendingAvatarDelete,
    setPendingAvatarDelete,
    setStatusMessage
}) => {
    if (pendingAvatarDelete) {
        try {
            if (formData.avatar_url) {
                const clean = formData.avatar_url.split('?')[0];
                await axios.post('/api/delete_avatar', { avatar_url: clean });
            }
        } catch (err) {
            console.error('Failed to delete avatar:', err);
        }
        setPendingAvatarDelete(false);
    }

    let avatar_url = formData.avatar_url || '';
    if (formData.image) {
        if (formData.avatar_url) {
            try {
                const clean = formData.avatar_url.split('?')[0];
                await axios.post('/api/delete_avatar', { avatar_url: clean });
            } catch (err) {
                console.error('Failed to delete previous avatar:', err);
            }
        }

        const imgData = new FormData();
        imgData.append('avatar', formData.image);
        imgData.append('chatbot_name', formData.title || 'default');

        try {
            const uploadRes = await axios.post('/api/upload_avatar', imgData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (uploadRes.data && uploadRes.data.avatar_url) {
                avatar_url = `${uploadRes.data.avatar_url}?t=${Date.now()}`;
                setFormData(prev => ({ ...prev, avatar_url, image: undefined }));
            } else {
                setStatusMessage('Error: Failed to upload avatar image.');
                return { success: false, avatar_url };
            }
        } catch (err) {
            setStatusMessage('Error: Failed to upload avatar image.');
            return { success: false, avatar_url };
        }
    }

    return { success: true, avatar_url };
};

export const handleAvatarCropComplete = ({ setCroppedAreaPixels, croppedAreaPixelsParam }) => {
    setCroppedAreaPixels(croppedAreaPixelsParam);
};

export const applyAvatarCrop = async ({
    cropImageSrc,
    croppedAreaPixels,
    pendingImageFile,
    uploadAvatarAndPersist,
    setStatusMessage,
    setShowCropDialog,
    setCropImageSrc,
    setPendingImageFile,
    setCrop,
    setZoom,
    setCroppedAreaPixels
}) => {
    try {
        if (!cropImageSrc || !croppedAreaPixels) {
            setShowCropDialog(false);
            setCropImageSrc(null);
            setPendingImageFile(null);
            return;
        }

        const diameter = 200;
        const blob = await getCroppedImg(cropImageSrc, croppedAreaPixels, diameter);
        const fileName = pendingImageFile?.name || 'avatar.jpg';
        const croppedFile = new File([blob], fileName, { type: blob.type || 'image/jpeg' });
        await uploadAvatarAndPersist(croppedFile);

        try {
            URL.revokeObjectURL(cropImageSrc);
        } catch (e) {
            // Ignore object URL cleanup failures.
        }
    } catch (err) {
        console.error('Error cropping image:', err);
        setStatusMessage('Failed to crop image.');
    } finally {
        setShowCropDialog(false);
        setCropImageSrc(null);
        setPendingImageFile(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
    }
};

export const cancelAvatarCrop = ({
    cropImageSrc,
    pendingImageFile,
    uploadAvatarAndPersist,
    setShowCropDialog,
    setCropImageSrc,
    setPendingImageFile,
    setCrop,
    setZoom,
    setCroppedAreaPixels
}) => {
    setShowCropDialog(false);

    if (cropImageSrc) {
        try {
            URL.revokeObjectURL(cropImageSrc);
        } catch (e) {
            // Ignore object URL cleanup failures.
        }
    }

    if (pendingImageFile) {
        uploadAvatarAndPersist(pendingImageFile);
    }

    setCropImageSrc(null);
    setPendingImageFile(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
};

export default getAvatarDisplayUrl;